/**
 * Pipeline Kanban — Phase 7G.1 (drag-and-drop enabled).
 *
 * Admin-key gated (ADMIN_API_KEY). Fetches /api/analytics/pipeline, renders
 * one droppable column per status, and each lead card is draggable via
 * @dnd-kit/core. On drop:
 *   1. optimistic local move
 *   2. PATCH /api/leads/{lead_id}/status
 *   3. on error → rollback + toast
 *
 * Advisor scoping (Phase 7G.1):
 *   If `lwp_advisor_slug` exists in localStorage, cards whose
 *   `assigned_advisor_slug` !== that slug are visible but drag-disabled
 *   (grayscale + not-allowed cursor).
 *
 * Dark mode (Phase 7G.5): uses admin-theme CSS vars declared in App.css.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core'
import { AdminThemeToggle, useAdminTheme } from './admin/theme'
import LeadDetailDrawer from './admin/LeadDetailDrawer'

const STATUS_LABELS = {
  new: 'New',
  partial: 'Partial',
  contacted: 'Contacted',
  qualified: 'Qualified',
  in_consultation: 'In Consultation',
  application_sent: 'Application Sent',
  underwriting: 'Underwriting',
  bound_issued: 'Bound / Issued',
  client: 'Client',
  closed_lost: 'Closed Lost',
}

const STATUS_ACCENT = {
  new: '#1E3A8A',
  partial: '#6B7280',
  contacted: '#0B2545',
  qualified: '#0369A1',
  in_consultation: '#0891B2',
  application_sent: '#7C3AED',
  underwriting: '#B45309',
  bound_issued: '#059669',
  client: '#065F46',
  closed_lost: '#991B1B',
}

const API_URL = process.env.REACT_APP_BACKEND_URL || ''

function LeadCard({ lead, canDrag, dragging, style, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
    disabled: !canDrag,
    data: { lead },
  })
  const opacity = isDragging ? 0.35 : 1
  const cursor = canDrag ? 'grab' : 'pointer'
  // Suppress the click after a drag ends — @dnd-kit fires a synthetic click
  // on pointerup even when the pointer moved. We gate on isDragging and on
  // a small "just-dragged" window handled by onPointerDown resetting a flag.
  const justDraggedRef = useRef(false)
  const onPointerDown = () => { justDraggedRef.current = false }
  const onDragStart = () => { justDraggedRef.current = true }
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onPointerDown={(e) => {
        if (listeners?.onPointerDown) listeners.onPointerDown(e)
        onPointerDown()
      }}
      onClick={(e) => {
        if (isDragging || justDraggedRef.current) return
        onOpen && onOpen(lead)
      }}
      data-testid={`pipeline-card-${lead.lead_id}`}
      style={{
        display: 'block',
        padding: 10,
        background: 'var(--admin-scrim)',
        borderRadius: 6,
        border: '1px solid var(--admin-border)',
        color: 'inherit',
        transition: 'background 120ms ease, opacity 120ms ease',
        opacity,
        cursor,
        userSelect: 'none',
        position: 'relative',
        ...(canDrag ? {} : { filter: 'grayscale(0.35)' }),
        ...(style || {}),
      }}
      title={canDrag ? 'Drag to change status · click to open details' : 'Click to open details · drag disabled'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-navy)' }}>{lead.name}</span>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#B45309',
            background: '#FEF3C7',
            padding: '1px 6px',
            borderRadius: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {lead.score}
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--admin-muted)', marginBottom: 6 }}>{lead.source || '—'}</div>
      {lead.top_products?.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--admin-slate)', marginBottom: 6 }}>
          {lead.top_products.join(' · ')}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--admin-muted)' }}>
        <span>{lead.assigned_advisor_name || 'Unassigned'}</span>
        <span>{lead.days_in_status}d in stage</span>
      </div>
      {/* Small affordance in the corner so the click target is discoverable */}
      <span
        data-testid={`pipeline-open-${lead.lead_id}`}
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 6,
          right: 8,
          fontSize: 14,
          color: 'var(--admin-muted)',
          fontWeight: 700,
          lineHeight: 1,
          pointerEvents: 'none',
        }}
      >
        ⋯
      </span>
    </div>
  )
}

function Column({ column, advisorSlug, onOpen }) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${column.status}` })
  return (
    <div
      ref={setNodeRef}
      style={{
        minWidth: 280,
        maxWidth: 280,
        background: 'var(--admin-surface)',
        borderRadius: 10,
        border: `1px solid ${isOver ? 'var(--admin-gold)' : 'var(--admin-border)'}`,
        padding: 12,
        transition: 'border-color 150ms ease',
      }}
      data-testid={`pipeline-column-${column.status}`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: 8,
          borderBottom: `2px solid ${STATUS_ACCENT[column.status] || '#0B2545'}`,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-slate)' }}>
          {STATUS_LABELS[column.status] || column.status}
        </span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--admin-muted)',
            background: 'var(--admin-scrim-hover)',
            padding: '2px 8px',
            borderRadius: 20,
          }}
        >
          {column.leads.length}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '65vh', overflowY: 'auto' }}>
        {column.leads.map((lead) => {
          const canDrag = !advisorSlug || lead.assigned_advisor_slug === advisorSlug
          return <LeadCard key={lead.id} lead={lead} canDrag={canDrag} onOpen={onOpen} />
        })}
        {column.leads.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--admin-muted)', textAlign: 'center', padding: '16px 0' }}>
            Empty
          </div>
        )}
      </div>
    </div>
  )
}

export default function AdminPipeline() {
  useAdminTheme() // ensures data-admin-theme is set on <html>
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('lwp_admin_key') || '')
  const [advisorSlug, setAdvisorSlug] = useState(() => localStorage.getItem('lwp_advisor_slug') || '')
  const [keyInput, setKeyInput] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [activeLead, setActiveLead] = useState(null)
  const [openLead, setOpenLead] = useState(null)
  const [toast, setToast] = useState(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const load = () => {
    if (!adminKey) return
    setLoading(true)
    setError(null)
    fetch(`${API_URL}/api/analytics/pipeline?admin_key=${encodeURIComponent(adminKey)}`)
      .then((r) => {
        if (r.status === 401) throw new Error('Invalid admin key')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }
  useEffect(load, [adminKey])

  const totals = useMemo(() => {
    if (!data) return 0
    return data.columns.reduce((n, c) => n + c.leads.length, 0)
  }, [data])

  const handleDragStart = (event) => {
    const lead = event.active?.data?.current?.lead
    if (lead) setActiveLead(lead)
  }

  const handleDragEnd = async (event) => {
    setActiveLead(null)
    const { active, over } = event
    if (!over) return
    const lead = active?.data?.current?.lead
    if (!lead) return
    const targetStatus = String(over.id).replace(/^col-/, '')
    if (!targetStatus || lead.status === targetStatus) return

    // Snapshot for rollback
    const snapshot = data
    // Optimistic move: locate the lead, move it to the target column, update its status
    const updated = { ...data, columns: data.columns.map((c) => ({ ...c, leads: [...c.leads] })) }
    let moved = null
    for (const col of updated.columns) {
      const idx = col.leads.findIndex((l) => l.id === lead.id)
      if (idx >= 0) {
        moved = col.leads.splice(idx, 1)[0]
        break
      }
    }
    if (!moved) return
    moved = { ...moved, status: targetStatus, days_in_status: 0 }
    const targetCol = updated.columns.find((c) => c.status === targetStatus)
    if (targetCol) targetCol.leads.unshift(moved)
    setData(updated)

    // Persist
    try {
      const res = await fetch(
        `${API_URL}/api/leads/${encodeURIComponent(lead.lead_id)}/status?admin_key=${encodeURIComponent(adminKey)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: targetStatus }),
        }
      )
      if (!res.ok) {
        const body = await res.text()
        throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`)
      }
      setToast({ kind: 'ok', text: `${lead.name} → ${STATUS_LABELS[targetStatus]}` })
    } catch (e) {
      setData(snapshot) // rollback
      setToast({ kind: 'err', text: `Move failed: ${e.message}` })
    }
    setTimeout(() => setToast(null), 3500)
  }

  if (!adminKey) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--admin-ivory)',
          fontFamily: 'Inter,-apple-system,sans-serif',
          padding: '24px',
          color: 'var(--admin-slate)',
        }}
        data-testid="pipeline-auth-gate"
      >
        <div style={{ maxWidth: 420, width: '100%' }}>
          <h1 style={{ fontFamily: 'Fraunces, serif', color: 'var(--admin-navy)', marginBottom: 8 }}>Pipeline</h1>
          <p style={{ color: 'var(--admin-muted)', marginBottom: 24, fontSize: 14 }}>
            Admin-only view. Enter your ADMIN_API_KEY to continue.
          </p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="ADMIN_API_KEY"
            data-testid="pipeline-key-input"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid var(--admin-border)',
              borderRadius: 8,
              fontSize: 14,
              marginBottom: 12,
              background: 'var(--admin-surface)',
              color: 'var(--admin-slate)',
            }}
          />
          <button
            onClick={() => {
              localStorage.setItem('lwp_admin_key', keyInput)
              setAdminKey(keyInput)
            }}
            data-testid="pipeline-key-submit"
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'var(--admin-navy-solid)',
              color: 'var(--admin-ivory-solid)',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Continue
          </button>
        </div>
      </div>
    )
  }

  if (loading)
    return (
      <div
        style={{ padding: 40, fontFamily: 'Inter,sans-serif', background: 'var(--admin-ivory)', minHeight: '100vh', color: 'var(--admin-slate)' }}
        data-testid="pipeline-loading"
      >
        Loading…
      </div>
    )
  if (error)
    return (
      <div
        style={{ padding: 40, fontFamily: 'Inter,sans-serif', background: 'var(--admin-ivory)', minHeight: '100vh', color: '#991B1B' }}
        data-testid="pipeline-error"
      >
        <h2>Error</h2>
        <p>{error}</p>
        <button
          onClick={() => {
            localStorage.removeItem('lwp_admin_key')
            setAdminKey('')
          }}
          style={{ marginTop: 12, padding: '8px 16px', background: 'var(--admin-navy-solid)', color: 'var(--admin-ivory-solid)', border: 'none', borderRadius: 6, cursor: 'pointer' }}
        >
          Change admin key
        </button>
      </div>
    )
  if (!data) return null

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--admin-ivory)',
        fontFamily: 'Inter,-apple-system,sans-serif',
        padding: '24px',
        color: 'var(--admin-slate)',
      }}
      data-testid="pipeline-view"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', color: 'var(--admin-navy)', margin: 0, fontSize: 32 }}>Pipeline</h1>
          <p style={{ color: 'var(--admin-muted)', margin: '4px 0 0 0', fontSize: 13 }}>
            {totals} lead{totals === 1 ? '' : 's'} across {data.columns.length} stages · Drag to change status
            {advisorSlug ? ` · Scoped to advisor: ${advisorSlug}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={advisorSlug}
            placeholder="advisor-slug scope (optional)"
            onChange={(e) => setAdvisorSlug(e.target.value)}
            onBlur={(e) => localStorage.setItem('lwp_advisor_slug', e.target.value)}
            data-testid="pipeline-advisor-scope"
            style={{
              padding: '6px 10px',
              fontSize: 12,
              border: '1px solid var(--admin-border)',
              borderRadius: 6,
              background: 'var(--admin-surface)',
              color: 'var(--admin-slate)',
              width: 220,
            }}
          />
          <AdminThemeToggle />
          <a
            href="/admin/collections/leads"
            style={{
              padding: '8px 16px',
              background: 'var(--admin-navy-solid)',
              color: 'var(--admin-ivory-solid)',
              textDecoration: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 500,
            }}
            data-testid="pipeline-open-list"
          >
            Open list in admin
          </a>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 24 }}>
          {data.columns.map((col) => (
            <Column key={col.status} column={col} advisorSlug={advisorSlug} onOpen={setOpenLead} />
          ))}
        </div>
        <DragOverlay>
          {activeLead ? (
            <div
              style={{
                padding: 10,
                background: 'var(--admin-surface)',
                borderRadius: 6,
                border: '1px solid var(--admin-gold)',
                boxShadow: '0 8px 24px rgba(11,37,69,0.25)',
                width: 256,
                pointerEvents: 'none',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-navy)' }}>{activeLead.name}</div>
              <div style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 4 }}>{activeLead.source || '—'}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {toast && (
        <div
          data-testid={`pipeline-toast-${toast.kind}`}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: toast.kind === 'ok' ? '#065F46' : '#991B1B',
            color: '#FAF8F3',
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'Inter,sans-serif',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            zIndex: 9999,
          }}
        >
          {toast.text}
        </div>
      )}

      {openLead && (
        <LeadDetailDrawer lead={openLead} adminKey={adminKey} onClose={() => setOpenLead(null)} />
      )}
    </div>
  )
}
